export const emailLogPath = '/api/public/logError';

function shouldIgnoreResourceError(source) {
  return typeof source === 'string'
    && source.indexOf('/content/Views/dashboard/images/icons/pj-scope-icons/') !== -1
    && /\.svg(\?|$)/i.test(source);
}

/**
 * Ignores opaque cross-origin script failures where the browser strips the real
 * filename and stack, so the error cannot be attributed to visualizer code.
 */
function isCrossOriginScriptError(event) {
  return !event.error
    && !event.filename
    && (event.message === 'Script error.' || event.message === 'Script error');
}

/**
 * Ignores a known legacy Custom HTML tag in GTM-K2RNL44 that calls global
 * jQuery on host pages where window.jQuery is not guaranteed.
 */
function isIgnoredGtmJqueryError(event) {
  const message = event.message || '';
  const source = event.filename || '';
  const stack = event.error && event.error.stack || '';
  const gtmContainerId = 'GTM-K2RNL44';

  return message.indexOf('jQuery is not defined') !== -1
    && (
      source.indexOf(gtmContainerId) !== -1
      || stack.indexOf(gtmContainerId) !== -1
      || source.indexOf('googletagmanager.com/gtm.js') !== -1
      || stack.indexOf('googletagmanager.com/gtm.js') !== -1
    );
}

/**
 * Window-level browser errors that should not trigger the fatal recovery popup
 * or email logging because they are known external script noise.
 */
function shouldIgnoreWindowError(event) {
  return isCrossOriginScriptError(event)
    || isIgnoredGtmJqueryError(event);
}

/**
 * Ignores intentionally canceled async work, usually aborted HTTP requests.
 */
export function isCanceledError(error) {
  if (!error) {
    return false;
  }

  const message = error.message || '';

  return error.name === 'CanceledError'
    || error.code === 'ERR_CANCELED'
    || message === 'canceled';
}

function reportWindowError(event, reportFatalError, captureResourceErrors) {
  const target = event.target || event.srcElement;

  if (target && target !== window && (target.src || target.href)) {
    if (!captureResourceErrors) {
      return;
    }

    const source = target.src || target.href;

    if (shouldIgnoreResourceError(source)) {
      return;
    }

    reportFatalError('resource-error', {
      name: 'ResourceError',
      message: 'Resource failed to load',
      source,
    });
    return;
  }

  if (shouldIgnoreWindowError(event)) {
    return;
  }

  reportFatalError('window-error', {
    message: event.message,
    name: (event.error && event.error.name) || 'Error',
    stack: event.error && event.error.stack,
    source: event.filename,
    line: event.lineno,
    column: event.colno,
  });
}

function reportUnhandledRejection(event, reportFatalError) {
  if (isCanceledError(event.reason)) {
    return;
  }

  reportFatalError('unhandled-rejection', event.reason || {
    message: 'Unhandled promise rejection',
  });
}

export function instrumentBrowserRuntime({reportFatalError, captureResourceErrors}) {
  window.addEventListener('error', (event) => reportWindowError(event, reportFatalError, captureResourceErrors), true);
  window.addEventListener('unhandledrejection', (event) => reportUnhandledRejection(event, reportFatalError));
}
